/// <amd-module name="CodeCoverage/CodeCoverageCiChangedTab/ChangedTabViewService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { ICoverageChangeResponse, IChangedTabDataProviderService } from "CodeCoverage/CodeCoverageCiChangedTab/ChangedTabDataProviderService";
import { IFileCoverageTableItem } from "CodeCoverage/ViewCommon/Contracts/Common";

// exported for testing
export const BATCH_SIZE = 100;

export interface ICodeCoverageChangeResponse {
    fileCoverageChanges: IFileCoverageTableItem[];
    hasMoreData: boolean;
}

export interface IChangedTabViewService extends IVssService {
    initialize(
        project: string,
        pipelineRunId: number,
        scope: string | undefined,
        fileCoverageChanges: IFileCoverageTableItem[],
        continuationToken?: string
    ): void;
    getFirstBatchedCoverageChanges(): ICodeCoverageChangeResponse;

    getBatchedCoverageChangesAsync(continuationToken?: number): Promise<ICodeCoverageChangeResponse>;

    getFilteredCoverageChangesAsync(searchText: string): Promise<IFileCoverageTableItem[]>;
}

// exported for testing
export class ChangedTabViewService extends VssService implements IChangedTabViewService {
    private project: string;
    private pipelineRunId: number;
    private scope: string | undefined;
    private continuationToken?: string;
    private fileCoverageChanges: IFileCoverageTableItem[];
    private fetchAllCoverageChangePromise: Promise<void> | undefined;

    public initialize(
        project: string,
        pipelineRunId: number,
        scope: string | undefined,
        fileCoverageChanges: IFileCoverageTableItem[],
        continuationToken?: string
    ): void {
        this.project = project;
        this.pipelineRunId = pipelineRunId;
        this.scope = scope;
        this.continuationToken = continuationToken;
        this.fileCoverageChanges = fileCoverageChanges;
        this.fetchAllCoverageChangePromise = undefined;
    }

    public getFirstBatchedCoverageChanges(): ICodeCoverageChangeResponse {
        return this.getBatchedCodeCoverageDataInternal(0);
    }

    // clientContinuationToken is the last index for which data is available;
    public getBatchedCoverageChangesAsync(clientContinuationToken?: number): Promise<ICodeCoverageChangeResponse> {
        const fileCoverageChanges: IFileCoverageTableItem[] = [];
        const startIndex = clientContinuationToken != undefined ? clientContinuationToken + 1 : 0;

        // Error Scenarios
        if (startIndex < 0 || startIndex > this.fileCoverageChanges.length) {
            return Promise.resolve({
                fileCoverageChanges,
                hasMoreData: false
            });
        }

        if ((startIndex < this.fileCoverageChanges.length && startIndex + BATCH_SIZE <= this.fileCoverageChanges.length) || !this.continuationToken) {
            return Promise.resolve(this.getBatchedCodeCoverageDataInternal(startIndex));
        } else {
            return this.fetchCoverageChangesFromDataProvider(this.continuationToken).then(() => this.getBatchedCodeCoverageDataInternal(startIndex));
        }
    }

    public getFilteredCoverageChangesAsync(searchText: string): Promise<IFileCoverageTableItem[]> {
        const lowerCaseSearchText = searchText.toLowerCase();

        return this.fetchAllCodeCoverageChanges().then(() => {
            return this.fileCoverageChanges.filter(item => {
                return item.filePath.toLowerCase().indexOf(lowerCaseSearchText) !== -1;
            });
        });
    }

    private fetchAllCodeCoverageChanges(): Promise<void> {
        if (!this.fetchAllCoverageChangePromise) {
            this.fetchAllCoverageChangePromise = this.fetchAllCodeCoverageChangesInternal();
        }

        return this.fetchAllCoverageChangePromise;
    }

    private fetchAllCodeCoverageChangesInternal(): Promise<void> {
        if (this.continuationToken) {
            return this.fetchCoverageChangesFromDataProvider(this.continuationToken).then(() => this.fetchAllCodeCoverageChangesInternal());
        }

        return Promise.resolve();
    }

    // Assumes the required data is already cached
    private getBatchedCodeCoverageDataInternal(startIndex: number): ICodeCoverageChangeResponse {
        const fileCoverageChanges: IFileCoverageTableItem[] = [];

        if (startIndex < this.fileCoverageChanges.length) {
            const end = startIndex + BATCH_SIZE <= this.fileCoverageChanges.length ? startIndex + BATCH_SIZE : undefined;
            this.fileCoverageChanges.slice(startIndex, end).forEach(item => fileCoverageChanges.push(item));

            return {
                fileCoverageChanges,
                hasMoreData: this.hasMoreData(startIndex)
            };
        }

        return {
            fileCoverageChanges,
            hasMoreData: false
        };
    }

    private fetchCoverageChangesFromDataProvider(continuationToken?: string): Promise<void> {
        return this.pageContext
            .getService<IChangedTabDataProviderService>("IChangedTabDataProviderService")
            .getCoverageChangesDataAsync(this.project, this.pipelineRunId, this.scope, continuationToken)
            .then((response: ICoverageChangeResponse) => {
                this.continuationToken = response.continuationToken;
                if (response.fileCoverageChanges) {
                    response.fileCoverageChanges.forEach(item => this.fileCoverageChanges.push(item));
                }
            });
    }

    private hasMoreData(startIndex: number): boolean {
        return startIndex + BATCH_SIZE < this.fileCoverageChanges.length || !!this.continuationToken;
    }
}

Services.add("IChangedTabViewService", { serviceFactory: ChangedTabViewService });
